import { Controller } from "@hotwired/stimulus"

export default class RevealController extends Controller {
  static values = {
    duration: { type: Number, default: 500 },
    // Milliseconds to hold before animating in. Lets a row of cards that all
    // cross the threshold together come in one after another instead of as a
    // single block.
    delay: { type: Number, default: 0 },
    origin: { type: String, default: "bottom" },
    distance: { type: Number, default: 24 },
  }

  declare readonly durationValue: number
  declare readonly delayValue: number
  declare readonly originValue: string
  declare readonly distanceValue: number

  private observer: IntersectionObserver | null = null
  private rafId: number | null = null
  private beforeCache!: () => void

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      (this.element as HTMLElement).style.opacity = "1"
      return
    }

    const el = this.element as HTMLElement

    // An element hidden at the current breakpoint (lg:hidden and friends) has
    // no layout box, so it can never intersect and the observer would never
    // fire — leaving it stuck at opacity 0 if the viewport later widens into
    // showing it. Leave those fully visible and don't observe them at all.
    if (el.getClientRects().length === 0) return

    el.style.opacity = "0"
    el.style.transform = `translateY(${this.translateY}px)`

    // Turbo snapshots the DOM for its page cache while these inline styles are
    // still applied, so a cached page would come back with everything that had
    // not yet revealed frozen at opacity 0. Hand the element back unstyled
    // before the snapshot is taken.
    this.beforeCache = () => this.reset()
    document.addEventListener("turbo:before-cache", this.beforeCache)

    this.observeWhenScrollSettles()
  }

  disconnect() {
    this.observer?.disconnect()
    if (this.rafId !== null) cancelAnimationFrame(this.rafId)
    if (this.beforeCache) document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  // Turbo replaces the body *before* it restores or resets the scroll position,
  // so at connect time window.scrollY is still wherever the previous page was
  // left. An observer created right then measures against that stale offset and
  // immediately fires for whatever happens to sit at that depth in the new
  // document — which is why arriving from a card link two thirds down a page
  // used to land on a page that had already finished animating.
  //
  // Waiting for the scroll position to hold still for a few frames covers both
  // that reset and the smooth-scrolling one (<html class="scroll-smooth">),
  // where the whole document glides past the viewport on the way to the top.
  private observeWhenScrollSettles() {
    let lastY = window.scrollY
    let stableFrames = 0
    // Never wait forever: if the visitor starts scrolling immediately, the
    // position never settles, and content must not stay invisible.
    const deadline = performance.now() + 1000

    const tick = () => {
      const y = window.scrollY
      if (y === lastY) {
        stableFrames++
      } else {
        stableFrames = 0
        lastY = y
      }

      if (stableFrames >= 3 || performance.now() > deadline) {
        this.rafId = null
        this.observe()
        return
      }
      this.rafId = requestAnimationFrame(tick)
    }

    this.rafId = requestAnimationFrame(tick)
  }

  private observe() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.animateIn()
            this.observer?.unobserve(this.element)
          }
        })
      },
      { threshold: 0, rootMargin: "0px 0px -10% 0px" }
    )

    this.observer.observe(this.element)
  }

  private get translateY(): number {
    return this.originValue === "top" ? -this.distanceValue : this.distanceValue
  }

  private reset() {
    const el = this.element as HTMLElement
    el.getAnimations().forEach((a) => a.cancel())
    el.style.opacity = ""
    el.style.transform = ""
  }

  private animateIn() {
    this.element.animate(
      [
        { opacity: "0", transform: `translateY(${this.translateY}px)` },
        { opacity: "1", transform: "translateY(0)" },
      ],
      {
        duration: this.durationValue,
        delay: this.delayValue,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        fill: "forwards",
      }
    )
  }
}
